import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowLeft, Inbox, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { WISSEL_BERICHT } from "@/lib/actiefTeam";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import PostbusBericht from "../components/PostbusBericht";
import BevestigAantal from "../components/BevestigAantal";
import { beslisGroep, openGroepen } from "@/lib/geheugenGroep";
import { GEHEUGEN, VANDAAG, beginStroom, gelezenIds, indeling, stroomVan, teller, vandaagIso } from "@/lib/postbusStromen";
import { useTaal } from "@/contexts/TaalContext";

/** De postbus. Hermes F onderdeel 3.
 *
 *  Wat geplande taken opleverden, nieuwste eerst. Een bericht telt als gelezen
 *  zodra het hier op glas heeft gestaan; een voorstel zonder besluit blijft in
 *  de teller in de zijbalk tot het is bevestigd of geweigerd.
 *
 *  B (opdracht Frank 23 okt): twee tabbladen, Vandaag en Geheugen, elk met een eigen teller;
 *  de ochtendronde van vandaag bovenaan en oudere ingeklapt; elke kaart zelf verwijderen, en
 *  per tabblad "Alles gelezen verwijderen" met eerst een vraag met het aantal. Een kaart telt
 *  pas als gelezen als zij in het open tabblad op glas staat.
 */
export default function PostbusPage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [berichten, setBerichten] = useState(null);
  const [fout, setFout] = useState("");
  // Herstelbatch b: de gekozen groepen (op bericht-id) en de vraag die eerst komt.
  const [gekozen, setGekozen] = useState(() => new Set());
  const [vraag, setVraag] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [stroom, setStroom] = useState(null);
  const [opruimVraag, setOpruimVraag] = useState(null);
  const gemeld = useRef(new Set());

  const haal = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/postbus`);
      setBerichten(r.data.berichten || []);
      setFout("");
      return r.data.berichten || [];
    } catch (err) {
      // GEEN STILLE LEGE POSTBUS: een fout is iets anders dan niets.
      setFout(err?.response?.data?.detail || t("De postbus kon niet worden opgehaald."));
      setBerichten([]);
      return [];
    }
  }, [t]);

  useEffect(() => {
    (async () => {
      const lijst = await haal();
      setStroom((oud) => oud || beginStroom(lijst));
    })();
  }, [haal]);

  // Wat in het OPEN tabblad op glas staat, telt als gelezen; het andere tabblad niet.
  // Herstelbatch a: een groepskaart draagt al haar berichten; die tellen samen.
  useEffect(() => {
    if (!berichten || !stroom) return;
    (async () => {
      for (const b of berichten.filter((x) => !x.gelezen && stroomVan(x) === stroom)) {
        for (const id of b.bericht_ids || [b.id]) {
          if (gemeld.current.has(id)) continue;
          gemeld.current.add(id);
          try {
            await axios.post(`${API}/postbus/${id}/gelezen`);
          } catch {
            // Blijft ongelezen; de teller laat hem dan staan, en dat is de veilige kant.
          }
        }
      }
    })();
  }, [berichten, stroom]);

  // K7e (19 okt): opruimvoorstellen horen bij de werkruimte; na een wissel opnieuw ophalen.
  useEffect(() => {
    const gewisseld = () => { haal(); };
    window.addEventListener(WISSEL_BERICHT, gewisseld);
    return () => window.removeEventListener(WISSEL_BERICHT, gewisseld);
  }, [haal]);

  const groepen = useMemo(() => openGroepen(berichten), [berichten]);
  const alleIds = groepen.flatMap((g) => g.ids);
  const gekozenIds = groepen.filter((g) => gekozen.has(g.id)).flatMap((g) => g.ids);

  const kies = (id, aan) => setGekozen((oud) => {
    const nieuw = new Set(oud);
    if (aan) nieuw.add(id); else nieuw.delete(id);
    return nieuw;
  });

  const voerUit = async () => {
    setBezig(true);
    try {
      await beslisGroep(vraag.ids, vraag.besluit);
      setGekozen(new Set());
      setVraag(null);
    } catch (err) {
      setFout(err?.response?.data?.detail || t("Er ging iets mis; er is niets veranderd."));
    } finally {
      setBezig(false);
      await haal();
    }
  };

  const verwijder = async (ids) => {
    try {
      await axios.post(`${API}/postbus/verwijder`, { ids });
    } catch (err) {
      setFout(err?.response?.data?.detail || t("Er ging iets mis; er is niets veranderd."));
    }
    setOpruimVraag(null);
    await haal();
  };

  const stelVoor = async (berichtId, taakId, soort) => {
    try {
      await axios.post(`${API}/postbus/${berichtId}/voorstel`, { taak_id: taakId, soort });
    } catch (err) {
      setFout(err?.response?.data?.detail || t("Het voorstel kon niet worden gemaakt."));
    }
    await haal();
  };

  return (
    <div className={`h-screen flex flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      <header className="flex items-center px-4 gap-3 border-b min-h-12 pt-[var(--veilig-boven)]">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="tap-target h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-serif text-lg font-light">
          <Inbox className="h-4 w-4 inline mr-2 text-[hsl(var(--accent))]" />
          {t("Postbus")}
        </h1>
      </header>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-2xl space-y-3 min-w-0">
          {fout && <p className="text-xs text-[hsl(var(--destructive))]" data-testid="postbus-fout">{fout}</p>}
          {berichten === null && <Loader2 className="h-4 w-4 animate-spin" />}
          {berichten?.length === 0 && !fout && (
            <p className="text-sm text-muted-foreground" data-testid="postbus-leeg">
              {t("Nog niets in de postbus. De ochtendronde komt elke dag om 07:30.")}
            </p>
          )}
          {berichten?.length > 0 && (
            <div className="flex gap-2 border-b" role="tablist" data-testid="postbus-tabs">
              {[[VANDAAG, "Vandaag"], [GEHEUGEN, "Geheugen"]].map(([s, kop]) => (
                <button key={s} type="button" role="tab" aria-selected={stroom === s}
                        data-testid={`postbus-tab-${s}`} onClick={() => { setStroom(s); setOpruimVraag(null); }}
                        className={`min-h-11 px-3 text-sm -mb-px border-b-2 ${stroom === s
                          ? "border-[hsl(var(--accent))] font-medium" : "border-transparent text-[hsl(var(--muted-foreground))]"}`}>
                  {t(kop)}
                  {teller(berichten, s) > 0 && (
                    <span className="ml-1.5 text-[10px] rounded-full border px-1.5" data-testid={`postbus-teller-${s}`}>
                      {teller(berichten, s)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {berichten?.length > 0 && stroom && (
            <div className="flex justify-end">
              {opruimVraag ? (
                <BevestigAantal
                  tekst={t("{n} gelezen kaarten verwijderen? Ze zijn daarna weg.", { n: opruimVraag.length })}
                  onJa={() => verwijder(opruimVraag)} onNee={() => setOpruimVraag(null)} />
              ) : (
                <Button size="sm" variant="outline" className="min-h-11 px-4" data-testid="postbus-gelezen-weg"
                        disabled={gelezenIds(berichten, stroom).length === 0}
                        onClick={() => setOpruimVraag(gelezenIds(berichten, stroom))}>
                  {t("Alles gelezen verwijderen ({n})", { n: gelezenIds(berichten, stroom).length })}
                </Button>
              )}
            </div>
          )}
          {stroom === GEHEUGEN && alleIds.length > 0 && (
            <section className="border rounded-lg p-3 space-y-2" data-testid="postbus-alles">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {t("Geheugen opruimen: {n} voorstellen in {g} groepen.", { n: alleIds.length, g: groepen.length })}
              </p>
              {vraag ? (
                <BevestigAantal
                  tekst={vraag.besluit === "vervangen"
                    ? t("{n} voorstellen vervangen? De regels die weggaan, zijn daarna weg.", { n: vraag.ids.length })
                    : t("{n} voorstellen houden? Alle regels blijven staan.", { n: vraag.ids.length })}
                  bezig={bezig} onJa={voerUit} onNee={() => setVraag(null)} />
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {gekozenIds.length > 0 ? (
                    <>
                      <Button size="sm" className="min-h-11 px-4" data-testid="postbus-selectie-vervangen"
                              onClick={() => setVraag({ besluit: "vervangen", ids: gekozenIds })}>
                        {t("Selectie vervangen ({n})", { n: gekozenIds.length })}
                      </Button>
                      <Button size="sm" variant="outline" className="min-h-11 px-4" data-testid="postbus-selectie-houden"
                              onClick={() => setVraag({ besluit: "houden", ids: gekozenIds })}>
                        {t("Selectie houden ({n})", { n: gekozenIds.length })}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" className="min-h-11 px-4" data-testid="postbus-alles-vervangen"
                              onClick={() => setVraag({ besluit: "vervangen", ids: alleIds })}>
                        {t("Alles vervangen ({n})", { n: alleIds.length })}
                      </Button>
                      <Button size="sm" variant="outline" className="min-h-11 px-4" data-testid="postbus-alles-houden"
                              onClick={() => setVraag({ besluit: "houden", ids: alleIds })}>
                        {t("Alles houden ({n})", { n: alleIds.length })}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </section>
          )}
          {stroom && (() => {
            const { boven, overig, eerder } = indeling(berichten, stroom, vandaagIso());
            const kaart = (b) => (
              <PostbusBericht key={b.id} bericht={b} onVoorstel={stelVoor} onBesloten={() => haal()}
                              gekozen={gekozen.has(b.id)} onKies={(aan) => kies(b.id, aan)}
                              onVerwijder={() => verwijder(b.bericht_ids || [b.id])} />
            );
            return (
              <>
                {boven && kaart(boven)}
                {overig.map(kaart)}
                {berichten?.length > 0 && !boven && !overig.length && !eerder.length && (
                  <p className="text-sm text-muted-foreground" data-testid="postbus-tab-leeg">{t("Niets in dit tabblad.")}</p>
                )}
                {eerder.length > 0 && (
                  <details className="border rounded-lg p-3" data-testid="postbus-eerder">
                    <summary className="text-sm cursor-pointer min-h-11 flex items-center">
                      {t("Eerdere rondes ({n})", { n: eerder.length })}
                    </summary>
                    <div className="space-y-3 mt-2">{eerder.map(kaart)}</div>
                  </details>
                )}
              </>
            );
          })()}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}
